import _capitalize from 'lodash/capitalize'

import { Application } from '@/graphql/generated'
import { User, Valuation } from '@/types/unlock'

export const userHasGroup = (user: User, name: string) => {
  return !!user?.groups?.find((group) => group.name === name)
}

export const isOpsManager = (user: User) => {
  return userHasGroup(user, 'underwriting')
}

export const isHeoManager = (user: User) => {
  return userHasGroup(user, 'heo-manager')
}

export const isCloserUser = (user: User) => {
  return userHasGroup(user, 'closing')
}

export const isPostCloserUser = (user: User) => {
  return userHasGroup(user, 'post-closing')
}

export const isUAMUser = (user: User) => {
  return userHasGroup(user, 'uam-review')
}

export const isUWUser = (user: User) => {
  return userHasGroup(user, 'uw-review')
}

export const isSalesUser = (user: User) => {
  return userHasGroup(user, 'sales')
}

export const isHSRUser = (user: User) => {
  return userHasGroup(user, 'homeowner-support-rep')
}

export const isSalesOnlyUser = (user: User) => {
  return userHasGroup(user, 'sales') && user?.groups?.length === 1
}
export const isProcessingUser = (user: User) => {
  return userHasGroup(user, 'processing')
}

export const isServicingUser = (user: User) => {
  return userHasGroup(user, 'servicing')
}

export const isUsersAdmin = (user: User) => {
  return userHasGroup(user, 'users-admin')
}

export const isSuperUser = (user: User) => {
  return user?.is_superuser
}

export const isTasksAdmin = (user: User) => {
  return userHasGroup(user, 'tasks-admin')
}

export const isStatesAdmin = (user: User) => {
  return userHasGroup(user, 'states-admin')
}

export const isBusinessContactsAdmin = (user: User) => {
  return userHasGroup(user, 'business-contacts-admin')
}

export const isUnderwritingUser = (user: User) => {
  return userHasGroup(user, 'underwriting')
}

export const isValuationUser = (user: User) => {
  return userHasGroup(user, 'valuations')
}

export const isLockOverride = (user: User) => {
  return userHasGroup(user, 'lock_override')
}

export const isTsRoundRobin = (user: User) => {
  return userHasGroup(user, 'ts-round-robin')
}

export const isWorkviewsAdminOrSuperuser = (user: User) => {
  return userHasGroup(user, 'allow_copy_export_data') || user?.is_superuser === true
}

export const isUserAllowedToSendToDocuSign = (user: User) => {
  return isHeoManager(user) || user?.is_superuser
}

export const dscrEditAllowed = (loggedUser) =>
  isHeoManager(loggedUser) ||
  isProcessingUser(loggedUser) ||
  isUAMUser(loggedUser) ||
  isUnderwritingUser(loggedUser) ||
  isSuperUser(loggedUser)

export const systemUsers = ['maximus@unlock.com', 'max@unlock.com', 'tray@unlock.com']

export function getUserFullNameOrEmail(user: User) {
  const [first, last, username] = [user.first_name, user.last_name, user.username]
  if (first && last) {
    return first.length > 2
      ? `${_capitalize(first)} ${_capitalize(last)}`
      : `${first.toUpperCase()} ${_capitalize(last)}`
  }
  if (first) {
    // Some permission group users have only first name
    return first
  }
  return username || 'no name'
}

export const staffUserGroupsAvailableInWorkviews = [
  'sales',
  'processing',
  'valuations',
  'uam-review',
  'uw-review',
  'underwriting',
  'closing',
  'post-closing',
]

export const staffUserGroupsAvailableInStreamingNotes = [
  'home-value-adjustment',
  'uam_review',
  'property-review',
  'uw_review',
  'dscr-review',
  '3rd-lien-review',
  'solar-review',
  'dti-review',
]

export const isSalesUserAndAppNotInPendingICSDelivery = ({ userGroups, applicationStateIndex }) =>
  // Pending ICS Delivery index is 12
  (userGroups.some((group) => group.name === 'sales') ||
    userGroups.some((group) => group.name === 'homeowner-support-rep')) &&
  applicationStateIndex !== 12

export const isAllowedToAssignSales = (user: User, application: Application) => {
  if (isSuperUser(user)) return true

  if (!application?.manuallyCreated) {
    return false
  }

  return isHeoManager(user) || isSalesUser(user) || isHSRUser(user)
}

export const assignDealTeamDisabled = (user, application) =>
  !isSuperUser(user) &&
  (isSalesUser(user) || isHSRUser(user)) &&
  !isHeoManager(user) &&
  !application?.manuallyCreated

/**
 * Determines if a user is allowed to edit a post-pending ICS signature based on the application state and user role.
 *
 * @param {string} applicationState - The current state of the application.
 * @param {User} user - The user object containing role information.
 * @returns {boolean} - Returns `true` if the user is allowed to edit, otherwise `false`.
 */
export const isAllowedToEditPostPendingIcsSignature = (
  applicationState: string,
  user: User
): boolean => {
  const blockedApplicationStates = [
    'pending-doc-draw',
    'pending-wire',
    'pending-signature',
    'pending-escrow-disbursement',
    'pending-recording',
    'closed-won',
    'closed-lost',
  ]
  const applicationStateIsAllowed = !blockedApplicationStates.includes(applicationState)
  if (applicationStateIsAllowed) return true

  return isPostCloserUser(user) || isCloserUser(user) || isSuperUser(user)
}
